import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Plus,
  Plug,
  Loader2,
  AlertCircle,
  RefreshCw,
  Globe,
  FileText,
  Bell,
  ChevronRight,
} from 'lucide-react';
import { CreateIntegrationDialog } from './components/CreateIntegrationDialog';
import { useListCuratedIntegrations } from '../../../api/curated-integrations.api';
import type {
  CuratedIntegrationListItemDto,
  ListCuratedIntegrationsFilter,
} from '../../../api/curated-integrations.types';

type StatusFilter = 'all' | 'draft' | 'published';

function StatusBadge({ isPublished }: { isPublished: boolean }) {
  if (isPublished) {
    return (
      <Badge
        className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1"
        aria-label="Status: Veröffentlicht"
      >
        <Globe className="w-3 h-3" aria-hidden="true" />
        Veröffentlicht
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-1"
      aria-label="Status: Entwurf"
    >
      <FileText className="w-3 h-3" aria-hidden="true" />
      Entwurf
    </Badge>
  );
}

function IntegrationRow({
  integration,
  onClick,
}: {
  integration: CuratedIntegrationListItemDto;
  onClick: () => void;
}) {
  const hasUnacknowledged = integration.unacknowledgedChangesCount > 0;

  return (
    <tr
      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer group"
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      tabIndex={0}
      role="row"
      aria-label={`Integration ${integration.displayName ?? integration.name} öffnen`}
    >
      {/* Icon */}
      <td className="py-3 px-4 w-12">
        {integration.iconUrl ? (
          <img
            src={integration.iconUrl}
            alt={`${integration.displayName ?? integration.name} Icon`}
            className="w-8 h-8 rounded-lg object-contain border border-gray-200 bg-white"
          />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100">
            <Plug className="w-4 h-4 text-blue-500" aria-hidden="true" />
          </div>
        )}
      </td>

      {/* Name */}
      <td className="py-3 px-4">
        <div className="font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
          {integration.displayName ?? integration.name}
        </div>
        {integration.displayName && (
          <div className="text-xs text-gray-400 font-mono mt-0.5">{integration.name}</div>
        )}
      </td>

      {/* Category */}
      <td className="py-3 px-4">
        {integration.category ? (
          <span className="text-sm text-gray-600">{integration.category}</span>
        ) : (
          <span className="text-sm text-gray-400 italic">—</span>
        )}
      </td>

      {/* Status */}
      <td className="py-3 px-4">
        <StatusBadge isPublished={integration.isPublished} />
      </td>

      {/* Connections count */}
      <td className="py-3 px-4 text-sm text-gray-600">
        {integration.connectionCount}
      </td>

      {/* Unacknowledged changes badge */}
      <td className="py-3 px-4">
        {hasUnacknowledged ? (
          <span
            className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-500 rounded-full px-2 py-0.5"
            aria-label={`${integration.unacknowledgedChangesCount} nicht bestätigte Änderungen`}
          >
            <Bell className="w-3 h-3" aria-hidden="true" />
            {integration.unacknowledgedChangesCount}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Chevron */}
      <td className="py-3 px-4 text-right">
        <ChevronRight
          className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors ml-auto"
          aria-hidden="true"
        />
      </td>
    </tr>
  );
}

/**
 * Liste aller Curated Integrations mit Tabelle, Status-Filter und Create-Dialog.
 * Route: /integrations
 */
export function IntegrationsListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);

  const apiFilter: ListCuratedIntegrationsFilter | undefined =
    statusFilter === 'all' ? undefined : { status: statusFilter };

  const { data: integrations, isLoading, error, refetch } = useListCuratedIntegrations(apiFilter);

  const handleRowClick = (id: string) => {
    navigate(`/integrations/${id}`);
  };

  const list = integrations ?? [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="w-6 h-6 text-blue-600" aria-hidden="true" />
            Integrationen
          </h1>
          <p className="text-gray-500 mt-1">
            Verwaltung der kuratierten MCP-Integrationen für Kunden
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          aria-label="Neue Integration anlegen"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          Neue Integration
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          role="alert"
          className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm">{error.message}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => void refetch()}
            aria-label="Liste neu laden"
          >
            Wiederholen
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
          aria-label="Nach Status filtern"
        >
          <SelectTrigger className="w-48" aria-label="Statusfilter">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="draft">Entwurf</SelectItem>
            <SelectItem value="published">Veröffentlicht</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          aria-label="Liste aktualisieren"
        >
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
        </Button>
        <span className="text-sm text-gray-500 ml-auto">
          {isLoading ? '' : `${list.length} Einträge`}
        </span>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" aria-label="Integrationen werden geladen…" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && list.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plug className="w-7 h-7 text-blue-400" aria-hidden="true" />
            </div>
            <p className="text-gray-700 font-semibold text-lg">Keine Integrationen gefunden</p>
            <p className="text-gray-400 text-sm mt-1">
              {statusFilter !== 'all'
                ? 'Versuchen Sie einen anderen Filter.'
                : 'Klicken Sie auf "Neue Integration" um die erste Integration anzulegen.'}
            </p>
            {statusFilter !== 'all' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setStatusFilter('all')}
              >
                Filter zurücksetzen
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && list.length > 0 && (
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                role="table"
                aria-label="Integrationen"
              >
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600 w-12">
                      <span className="sr-only">Icon</span>
                    </th>
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600">
                      Name
                    </th>
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600">
                      Kategorie
                    </th>
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600">
                      Status
                    </th>
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600">
                      Verbindungen
                    </th>
                    <th scope="col" className="py-3 px-4 text-left font-semibold text-gray-600">
                      Offene Änderungen
                    </th>
                    <th scope="col" className="py-3 px-4 w-8">
                      <span className="sr-only">Öffnen</span>
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {list.map((integration) => (
                    <IntegrationRow
                      key={integration.id}
                      integration={integration}
                      onClick={() => handleRowClick(integration.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <CreateIntegrationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refetch()}
      />
    </div>
  );
}
